import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { NOOP } from '../../utils/const';
import CollapseIcon from '../Icons/CollapseIcon';
import ExpandIcon from '../Icons/ExpandIcon';

/**
 * Gestione dello stato open/close.
 * @param {bool | null} open se open non è definito, lo stato è gestito internamente altrimenti open definisce lo stato della card.
 * @param {bool} defaultClosed definisce lo stato inziale.
 * @param {func} onOpen callback attivata se lo stato cambia da chiuso a aperto.
 * @param {func} onClose callback attivata se lo stato cambia da aperto a chiuso.
 */
function useOpenClose(open, defaultClosed, onOpen, onClose) {
  const [intOpen, setIntOpen] = useState(!defaultClosed);

  const controlled = open !== null && open !== undefined;
  const setOpen = useCallback((value, evt) => {
    if (!controlled) setIntOpen(value);
    if (value) {
      onOpen(evt);
    } else {
      onClose(evt);
    }
  }, [controlled, onClose, onOpen]);

  return [controlled ? open : intOpen, setOpen];
}

const Card = ({
  defaultClosed,
  children,
  collapsible,
  action,
  breadCrumb,
  className,
  onAction,
  fullBody,
  multipleActions,
  title,
  open: openProp,
  onOpen,
  onClose,
}) => {
  const [open, setOpen] = useOpenClose(openProp, defaultClosed, onOpen, onClose);

  const classes = useMemo(() => cns(
    'card',
    {
      'card--open': open,
      'card--close': !open,
      'card--fullBody': fullBody,
    },
    className,
  ), [open, fullBody, className]);

  return (
    <div className={classes}>
      <div className="card__header">
        {title && (
          <div className="card__title">
            {breadCrumb && <div className="card__breadcrumb">{breadCrumb}</div>}
            {title}
          </div>
        )}
        {collapsible && (
          <div className="card__icons">
            {open
              ? (
                <CollapseIcon
                  className="card__toggle"
                  onClick={(evt) => setOpen(false, evt)}
                  viewBox="0 0 50 50"
                />
              )
              : (
                <ExpandIcon
                  className="card__toggle"
                  onClick={(evt) => setOpen(true, evt)}
                  viewBox="0 0 50 50"
                />
              )}
          </div>
        )}
        {multipleActions && <div className="card__multipleActions">{multipleActions}</div>}
        {action && onAction && (
          <div
            className="card__action"
            role="presentation"
            onClick={onAction}
          >
            {action}
          </div>
        )}
      </div>
      <div className="card__body" aria-hidden={!open}>
        <div className="card__content">{children}</div>
      </div>
    </div>
  );
};

Card.propTypes = {
  /** Il testo dell'azione di default della card */
  action: PropTypes.string,
  /** Un breadCrumb opzionale da far comparire sopra il titolo */
  breadCrumb: PropTypes.node,
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica se la card è collassabile, ovvero può essere aperta e chiusa */
  collapsible: PropTypes.bool,
  /** Indica se la card è chiusa di default */
  defaultClosed: PropTypes.bool,
  /** Indica se il contenuto della card deve espandersi fino al bordo senza padding */
  fullBody: PropTypes.bool,
  /** Se alla card sono associate più azioni qui possiamo
     * specificare cosa mostrare all'utente come azioni della card */
  multipleActions: PropTypes.node,
  /**
   * La callback da chiamare quando viene invocata l'azione primaria:
   * ```
   *  onAction(event)
   * ```
   * dove:
   * * *evento*: evento scatenante
   */
  onAction: PropTypes.func,
  /** Il titolo da associare alla card */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),

  /**
Callback attivata quando la card viene aperta. Parametro: evento.

La `Card` deve essere `collapsible`.
   */
  onOpen: PropTypes.func,

  /**
Callback attivata quando la card viene chiusa. Parametro: evento.

La `Card` deve essere `collapsible`.
   */
  onClose: PropTypes.func,

  /**
Se la proprietà non è definita, la gestione dello stato è demandata al componente.
Se la proprietà è `true`/`false` definisce lo stato della card (aperto/chiuso).
   */
  open: PropTypes.bool,
};

Card.defaultProps = {
  action: '',
  breadCrumb: null,
  children: null,
  className: '',
  collapsible: false,
  defaultClosed: false,
  fullBody: false,
  multipleActions: null,
  onAction: null,
  title: '',

  open: null,
  onOpen: NOOP,
  onClose: NOOP,
};

export default Card;
